import React, { useEffect, useState } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { ArrowLeft, Briefcase, Calendar, CheckCircle, MapPin } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { SEOHead } from "./SEOHead";
import { RichTextContent } from "./RichTextContent";
import { getProjectById } from "../services/Project";
import { safeUrl, toSlug } from "../services/SiteSettings";
import { htmlToText } from "../services/richText";

const SITE_URL = "https://uspconsultancy.com.np";

export const projectPath = (project: { id?: number | string; title?: string }) =>
  `/project/${toSlug(project.title || "")}-${project.id}`;

interface ProjectDetailsPageProps {
  projectId: string | null;
}

export function ProjectDetailsPage({ projectId }: ProjectDetailsPageProps) {
  const [project, setProject] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    getProjectById(projectId)
      .then((data) => {
        if (!active) return;
        const p = data?.project || data;
        if (p?.id != null) setProject(p);
        else setNotFound(true);
      })
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [projectId]);

  if (loading) {
    return <div className="min-h-screen py-16 text-center text-gray-600">Loading project...</div>;
  }

  if (notFound || !project) {
    return (
      <div className="min-h-screen py-16 text-center">
        <h1 className="mb-4">Project not found</h1>
        <p className="text-gray-600 mb-6">This project may have been removed or the link is incorrect.</p>
        <Button asChild>
          <a href="/projects">View all projects</a>
        </Button>
      </div>
    );
  }

  const url = `${SITE_URL}${projectPath(project)}`;
  const plainDescription = htmlToText(project.description);
  const summary = plainDescription.length > 200 ? `${plainDescription.slice(0, 200).trimEnd()}…` : plainDescription;
  const imageUrl = safeUrl(project.imageUrl);
  const tags: string[] = (Array.isArray(project.tags) ? project.tags : []).filter((t: string) => t?.trim());

  return (
    <div className="min-h-screen py-12">
      <SEOHead
        title={`${project.title} - Project | USP Consultancy Nepal`}
        description={summary || `Consulting project by USP Consultancy: ${project.title}`}
        canonical={url}
        ogImage={imageUrl || undefined}
        keywords={`${project.category ?? ""}, ${project.typeOfAssignment ?? ""}, USP Consultancy projects Nepal, development consulting Nepal`}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: project.title,
            description: plainDescription,
            url,
            creator: { "@type": "Organization", name: "USP Consultancy", url: `${SITE_URL}/` },
            locationCreated: { "@type": "Place", name: project.location || "Nepal" },
            ...(imageUrl && { image: imageUrl }),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Projects", item: `${SITE_URL}/projects` },
              { "@type": "ListItem", position: 3, name: project.title, item: url },
            ],
          },
        ]}
      />

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a href="/projects" className="inline-flex items-center text-sm text-blue-600 hover:underline mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" />
          All projects
        </a>

        <header className="mb-8">
          <h1 className="mb-4">{project.title}</h1>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-6 text-sm text-gray-600">
            {project.year && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {project.year}
              </span>
            )}
            {project.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {project.location}
              </span>
            )}
            {project.typeOfAssignment && (
              <span className="flex items-center gap-1">
                <Briefcase className="w-4 h-4" />
                {project.typeOfAssignment}
              </span>
            )}
          </div>
        </header>

        {imageUrl && (
          <div className="mb-8 overflow-hidden rounded-lg bg-gray-50">
            <ImageWithFallback src={imageUrl} alt={project.title} className="w-full max-h-[480px] object-contain" />
          </div>
        )}

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">Project Description</h2>
          <RichTextContent value={project.description} className="text-gray-700 leading-relaxed" />
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          {project.category && (
            <div>
              <h3 className="font-semibold mb-2">Thematic Area</h3>
              <Badge variant="secondary">{project.category}</Badge>
            </div>
          )}
          {project.typeOfAssignment && (
            <div>
              <h3 className="font-semibold mb-2">Type of Assignment</h3>
              <Badge variant="outline">{project.typeOfAssignment}</Badge>
            </div>
          )}
          {project.completedYear && (
            <div>
              <h3 className="font-semibold mb-2">Completed Year</h3>
              <p className="text-gray-700">{project.completedYear}</p>
            </div>
          )}
          {project.location && (
            <div>
              <h3 className="font-semibold mb-2">Client</h3>
              <p className="text-gray-700">{project.location}</p>
            </div>
          )}
        </section>

        {tags.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">Key Highlights</h2>
            <ul className="space-y-2">
              {tags.map((tag, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-4 h-4 text-blue-600 mt-1 shrink-0" />
                  <span>{tag}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-12 text-center bg-gray-50 rounded-lg p-8">
          <h2 className="mb-4">Have a Project in Mind?</h2>
          <p className="text-gray-600 mb-6">Contact us to discuss how we can support your next assignment.</p>
          <Button size="lg" asChild>
            <a href="/contact">Start a Conversation</a>
          </Button>
        </div>
      </article>
    </div>
  );
}
